/**
 * Pure pricing math for the OneTapReply location calculator.
 *
 * Mirrors the current Stripe model:
 *   - Every location: flat $19.99/mo
 *   - Annual billing: $199.90/year per location
 *
 * Cents-based to avoid float drift. UI converts on display only.
 */

export const PRICE_CENTS_PER_LOCATION = 1999; // $19.99
export const ANNUAL_PRICE_CENTS_PER_LOCATION = 19990; // $199.90
export const PRICE_CENTS_LOCATION_1 = PRICE_CENTS_PER_LOCATION;
export const PRICE_CENTS_LOCATION_2 = PRICE_CENTS_PER_LOCATION;
export const PRICE_CENTS_LOCATION_3_PLUS = PRICE_CENTS_PER_LOCATION;

export type PricingLineItem = {
  position: number;          // 1-indexed location number
  unitCents: number;
  unitLabel: string;         // e.g. "$19.99"
  discountLabel: string;
};

export type PricingBreakdown = {
  lineItems: PricingLineItem[];
  totalCents: number;
  totalLabel: string;        // e.g. "$39.98"
  perLocationAvgCents: number; // total / locations, for "avg" display if needed
  currency: string;
};

export function getTierPriceCents(position: number): number {
  if (position < 1) throw new Error("position must be >= 1");
  return PRICE_CENTS_PER_LOCATION;
}

export function getTierDiscountLabel(position: number): string {
  if (position < 1) throw new Error("position must be >= 1");
  return "";
}

export function formatCurrency(cents: number, currency: string = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function calculateBreakdown(
  locations: number,
  currency: string = "USD"
): PricingBreakdown {
  if (locations < 1) throw new Error("locations must be >= 1");

  const lineItems: PricingLineItem[] = [];
  let totalCents = 0;

  for (let i = 1; i <= locations; i++) {
    const unitCents = getTierPriceCents(i);
    totalCents += unitCents;
    lineItems.push({
      position: i,
      unitCents,
      unitLabel: formatCurrency(unitCents, currency),
      discountLabel: getTierDiscountLabel(i),
    });
  }

  return {
    lineItems,
    totalCents,
    totalLabel: formatCurrency(totalCents, currency),
    perLocationAvgCents: Math.round(totalCents / locations),
    currency,
  };
}
